import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import ModalSidebarNav from './ModalSidebarNav';
import useSetupProgress from '../hooks/useSetupProgress';
import {
  Rocket,
  Check,
  ArrowRight,
  Sparkles,
  Shield,
  Users,
  Package,
  Zap,
  FileText,
  CreditCard,
  Link2,
  Code,
  QrCode,
  Calendar,
  CalendarCheck,
  Mail,
  Tag,
  Webhook,
  Key,
  Settings,
  UserCheck,
  Star,
  Store,
  PenTool,
  TicketCheck,
  Gift,
  LayoutDashboard,
  TrendingUp,
  PieChart,
  Receipt,
  UserPlus,
  Bell,
  Sliders,
  Target,
  Hand,
  Clock,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────────
 * Primitives
 * ────────────────────────────────────────────────────────────────────── */

const Bullet = ({ children }) => (
  <li className="flex gap-2.5 text-sm text-gray-700">
    <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
    <span className="leading-relaxed">{children}</span>
  </li>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-1.5">
      <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-5 ${className}`}>{children}</div>
);

const PathHint = ({ children }) => (
  <div className="mt-4 text-xs text-gray-500">
    <span className="font-medium text-gray-700">Where:</span> {children}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 * Admin: Setup home (only shown to admins/owners)
 * ────────────────────────────────────────────────────────────────────── */
const SetupHome = ({ progress, onNavigate, onClose }) => {
  const { completedCore, totalCore, progressPercent, hasStripeConnect, stripeIsLive } = progress;
  const allDone = completedCore === totalCore;

  const go = (view) => {
    onNavigate?.(view);
    onClose?.();
  };

  const phases = [
    {
      name: 'Build your business',
      steps: [
        { id: 'asset', title: 'Register an asset', desc: 'Boats, venues, vehicles — whatever you book against.', icon: Package, complete: progress.hasAsset, to: 'assets' },
        { id: 'role', title: 'Create a role', desc: 'Define positions like Captain, Guide, or Instructor.', icon: Shield, complete: progress.hasRole, to: 'roles' },
        { id: 'team', title: 'Add a team member', desc: 'Add yourself or staff — they get their own logins.', icon: Users, complete: progress.hasTeamMember, to: 'teamMembers' },
        { id: 'service', title: 'Create a service', desc: 'Build the bookable experience your customers buy.', icon: Zap, complete: progress.hasService, to: 'services' },
      ],
    },
    {
      name: 'Connect payments',
      steps: [
        { id: 'waiver', title: 'Set up your waiver', desc: 'Liability text customers sign before their booking.', icon: FileText, complete: progress.hasWaiverText, to: 'waivers' },
        { id: 'stripe', title: 'Connect Stripe', desc: 'Payments go straight to your Stripe — we never hold funds.', icon: CreditCard, complete: progress.hasStripeConnect, to: 'payments' },
      ],
    },
    {
      name: 'Take bookings',
      steps: [
        { id: 'flow', title: 'Create a booking flow', desc: 'Embed on your site, share a link, or QR it on signage.', icon: Link2, complete: progress.hasFlow, to: 'flows' },
      ],
    },
  ];

  return (
    <div>
      <SectionHeader
        icon={Rocket}
        title="Setup"
        subtitle={allDone ? "You're all set. Optional steps below." : 'Finish setup to start taking real bookings.'}
      />

      {/* Progress card */}
      <div className="rounded-2xl bg-gray-900 text-white p-6 mb-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-1">Setup progress</div>
              <div className="text-3xl font-semibold tracking-tight">
                {completedCore}<span className="text-gray-500 text-xl"> / {totalCore}</span>
              </div>
            </div>
            <div className="text-sm text-gray-400">{progressPercent}%</div>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
              data-testid="setup-progress-bar"
            />
          </div>
          {hasStripeConnect && !stripeIsLive && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-amber-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Stripe is in test mode — switch to live when you're ready.
            </div>
          )}
        </div>
      </div>

      <div className="space-y-7">
        {phases.map((phase) => (
          <div key={phase.name}>
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2.5">{phase.name}</div>
            <div className="space-y-1.5">
              {phase.steps.map((step) => {
                const Icon = step.icon;
                return (
                  <button
                    key={step.id}
                    onClick={() => go(step.to)}
                    data-testid={`help-setup-step-${step.id}`}
                    className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-all group ${
                      step.complete
                        ? 'bg-emerald-50/60 hover:bg-emerald-50'
                        : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      step.complete ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 group-hover:bg-gray-900 group-hover:text-white transition-colors'
                    }`}>
                      {step.complete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${step.complete ? 'text-emerald-900' : 'text-gray-900'}`}>{step.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{step.desc}</div>
                    </div>
                    <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all ${
                      step.complete ? 'text-emerald-600' : 'text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5'
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2.5">Optional — when you're ready</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <button onClick={() => go('marketplace')} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group" data-testid="help-setup-marketplace">
              <Store className="w-4 h-4 text-gray-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">List on Marketplace</div>
                <div className="text-xs text-gray-500">Public profile + listing</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all" />
            </button>
            <button onClick={() => go('blog')} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group" data-testid="help-setup-blog">
              <PenTool className="w-4 h-4 text-gray-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">Generate SEO content</div>
                <div className="text-xs text-gray-500">AI blog posts for visibility</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Team-member welcome — when admin setup home isn't relevant
 * ────────────────────────────────────────────────────────────────────── */
const WelcomeHome = ({ availableTabs, onNavigate, onClose, displayName }) => {
  const go = (view) => {
    onNavigate?.(view);
    onClose?.();
  };

  return (
    <div>
      <SectionHeader
        icon={Hand}
        title={`Welcome${displayName ? `, ${displayName}` : ''}`}
        subtitle="Quick orientation to the tabs you have access to."
      />

      <Card>
        <p className="text-sm text-gray-600 leading-relaxed">
          Your admin set up your access. Below is everything you can see — tap any of
          them to jump straight in, or pick a section in the sidebar for a deeper guide.
        </p>
      </Card>

      <div className="mt-6 space-y-1.5">
        <div className="text-xs uppercase tracking-[0.14em] text-gray-500 font-medium mb-2">Your tabs</div>
        {availableTabs.length === 0 && (
          <Card>
            <p className="text-sm text-gray-500">
              No tabs assigned yet. Ask your admin to grant access from the Roles tab.
            </p>
          </Card>
        )}
        {availableTabs.map((t) => {
          const Icon = t.icon || LayoutDashboard;
          return (
            <button
              key={t.id}
              onClick={() => go(t.navId || t.id)}
              data-testid={`help-welcome-tab-${t.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group bg-white"
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100 text-gray-700 group-hover:bg-gray-900 group-hover:text-white transition-colors flex-shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">{t.blurb}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Tab content sections — keyed by dashboard nav `id` for 1:1 mapping
 * ────────────────────────────────────────────────────────────────────── */

const HomeContent = () => (
  <div>
    <SectionHeader icon={LayoutDashboard} title="Dashboard" subtitle="Today's operations at a glance." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Your home view. Snapshot of today's bookings, revenue, leads waiting on
        follow-up, and the upcoming schedule. Use the date filter to zoom into a week,
        month, or season.
      </p>
      <ul className="space-y-2">
        <Bullet>Net revenue + completed/upcoming bookings</Bullet>
        <Bullet>Revenue chart with monthly breakdown</Bullet>
        <Bullet>New leads waiting on a reply</Bullet>
        <Bullet>Upcoming schedule with quick check-in</Bullet>
      </ul>
    </Card>
  </div>
);

const RolesContent = () => (
  <div>
    <SectionHeader icon={Shield} title="Roles" subtitle="The backbone of role-based access." />
    <Card>
      <div className="text-sm font-medium text-gray-900 mb-2">What a role is</div>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A <strong>Role</strong> is a position at your company (Captain, Guide,
        Front-desk). Each role carries its own permissions — what dashboard tabs are
        visible, and inside each tab whether the member can <strong>read</strong>,{' '}
        <strong>create</strong>, <strong>edit</strong>, or <strong>delete</strong>.
      </p>
      <ul className="space-y-2">
        <Bullet>Per-tab read / create / edit / delete toggles</Bullet>
        <Bullet>Pay rates per role (hourly, per-booking, or salary)</Bullet>
        <Bullet>Bind a role to specific services so it only sees relevant trips</Bullet>
        <Bullet>Change a role and every assigned team member updates instantly</Bullet>
      </ul>
      <PathHint>Operations → Roles</PathHint>
    </Card>
  </div>
);

const TeamContent = () => (
  <div>
    <SectionHeader icon={Users} title="Team" subtitle="The people behind the bookings." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Team members are the staff who help run trips. Each one gets their own login,
        sees only what their role permits, and can claim trips, log hours, and check
        guests in.
      </p>
      <ul className="space-y-2">
        <Bullet>Email or phone-based invite — they set their own password</Bullet>
        <Bullet>Optional passkey for fast, secure login</Bullet>
        <Bullet>Time-clock with GPS for hourly staff</Bullet>
        <Bullet>Approval queue for self-signup requests</Bullet>
      </ul>
      <PathHint>Operations → Team</PathHint>
    </Card>
  </div>
);

const AssetsContent = () => (
  <div>
    <SectionHeader icon={Package} title="Assets" subtitle="The things you book against." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Anything with finite availability — a boat, a room, a vehicle. Bookings schedule
        against assets so the system never double-books.
      </p>
      <ul className="space-y-2">
        <Bullet>Per-asset capacity and overbooking prevention</Bullet>
        <Bullet>Link assets to specific services (e.g. only 24ft boats run this tour)</Bullet>
        <Bullet>Photos, specs, registration numbers, maintenance notes</Bullet>
        <Bullet>Per-asset availability windows and blackout dates</Bullet>
      </ul>
      <PathHint>Operations → Assets</PathHint>
    </Card>
  </div>
);

const ServicesContent = () => (
  <div>
    <SectionHeader icon={Zap} title="Services" subtitle="What customers actually book." />
    <Card>
      <div className="text-sm font-medium text-gray-900 mb-2">Two capacity modes</div>
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-1">Per-ticket</div>
          <p className="text-xs text-gray-600 leading-relaxed">Sell individual seats up to capacity. Group tours, classes, events.</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-1">Per-asset (exclusive)</div>
          <p className="text-xs text-gray-600 leading-relaxed">One booking takes the whole asset. Boat charters, vehicle rentals.</p>
        </div>
      </div>
      <div className="text-sm font-medium text-gray-900 mb-2">Pricing & rules</div>
      <ul className="space-y-2">
        <Bullet>Flat or per-person pricing</Bullet>
        <Bullet>Day-of-week and seasonal rates</Bullet>
        <Bullet>Add-ons (fixed amount or % of base)</Bullet>
        <Bullet>Deposits — fixed amount or % of total</Bullet>
        <Bullet>Custom intake questions per service</Bullet>
        <Bullet>Sales tax, service fees, gratuity toggles</Bullet>
      </ul>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        AI-assisted creation
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        The Create Service wizard can draft your description and suggest pricing based
        on similar services in your niche. AI drafts, you approve.
      </p>
      <PathHint>Operations → Services → New service</PathHint>
    </Card>
  </div>
);

const ScheduleMineContent = () => (
  <div>
    <SectionHeader icon={CalendarCheck} title="My Schedule" subtitle="Just your trips." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A filtered view of the calendar showing only bookings assigned to you. Tap a
        trip for the brief, customer contact info, and a one-tap check-in.
      </p>
      <ul className="space-y-2">
        <Bullet>Today, week, and month views</Bullet>
        <Bullet>Tap a trip for service details and customer info</Bullet>
        <Bullet>One-tap check-in from the calendar</Bullet>
      </ul>
      <PathHint>Operations → My Schedule</PathHint>
    </Card>
  </div>
);

const BookingsContent = () => (
  <div>
    <SectionHeader icon={Calendar} title="Bookings & Calendar" subtitle="Every trip on one timeline." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Day / week / month views</Bullet>
        <Bullet>Filter by service, asset, or team member</Bullet>
        <Bullet>Drag to reschedule — emails the customer automatically</Bullet>
        <Bullet>Color-coded per service</Bullet>
        <Bullet>Subscribe via iCal/Google (read-only feed)</Bullet>
      </ul>
      <PathHint>Operations → Bookings</PathHint>
    </Card>
  </div>
);

const AssignmentsContent = () => (
  <div>
    <SectionHeader icon={TicketCheck} title="Assignments" subtitle="Claim the trips you're qualified for." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        When a booking needs staff, it shows up here. Qualified team members claim it
        directly, or an admin assigns from the booking. Once claimed, it moves to your
        "My Schedule".
      </p>
      <ul className="space-y-2">
        <Bullet>Unclaimed trips show first; claimed trips slide down</Bullet>
        <Bullet>Every card shows service, asset, time, and party size</Bullet>
        <Bullet>You only see what your role qualifies you for</Bullet>
      </ul>
      <PathHint>Operations → Assignments</PathHint>
    </Card>
  </div>
);

const FlowsContent = () => (
  <div>
    <SectionHeader icon={Link2} title="Booking Flows" subtitle="How customers reach you." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A <strong>Flow</strong> is a configured booking widget — pick the services it
        shows, set the branding, and surface it three ways:
      </p>
      <div className="grid sm:grid-cols-3 gap-3 mb-2">
        <div className="rounded-lg bg-gray-50 p-3">
          <Code className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">Embed</div>
          <div className="text-xs text-gray-500 mt-0.5">One snippet on your site</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <Link2 className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">Direct link</div>
          <div className="text-xs text-gray-500 mt-0.5">DMs, bios, emails</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <QrCode className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">QR code</div>
          <div className="text-xs text-gray-500 mt-0.5">Print on signage</div>
        </div>
      </div>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Card styles</div>
      <ul className="space-y-2">
        <Bullet><strong>Classic</strong> — clean image-on-top card with price chip</Bullet>
        <Bullet><strong>Flat</strong> — minimal white card, no shadows</Bullet>
        <Bullet><strong>Premium</strong> — full-bleed photo with overlay</Bullet>
        <Bullet><strong>Ticket</strong> — perforated-edge "stub" look</Bullet>
      </ul>
      <PathHint>Operations → Flows</PathHint>
    </Card>
  </div>
);

const PaymentsContent = () => (
  <div>
    <SectionHeader icon={CreditCard} title="Payments" subtitle="Stripe Connect, direct to you." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Funds land in <strong>your</strong> Stripe account on your normal payout schedule.
        BookingFlows never holds your money — we use Stripe Connect Direct Charges.
      </p>
      <ul className="space-y-2">
        <Bullet>Cards, Apple Pay, Google Pay — crypto if your Stripe supports it</Bullet>
        <Bullet>Deposits (fixed or %) with later balance-due links</Bullet>
        <Bullet>Refunds processed directly inside the booking</Bullet>
        <Bullet>Multi-currency — Stripe minimums enforced per-currency</Bullet>
        <Bullet>Platform & partner fees split server-side at charge time</Bullet>
      </ul>
      <PathHint>Financials → Overview</PathHint>
    </Card>
  </div>
);

const ProfitLossContent = () => (
  <div>
    <SectionHeader icon={PieChart} title="Profit & Loss" subtitle="Month over month." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Revenue and expense totals side-by-side</Bullet>
        <Bullet>Category breakdown (services, add-ons, fees, refunds)</Bullet>
        <Bullet>Comparison vs. previous period</Bullet>
        <Bullet>CSV export for your accountant</Bullet>
      </ul>
      <PathHint>Financials → P&L</PathHint>
    </Card>
  </div>
);

const RevenueContent = () => (
  <div>
    <SectionHeader icon={TrendingUp} title="Revenue" subtitle="Where the money came from." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Filter by service, asset, channel, or partner</Bullet>
        <Bullet>Actual vs projected revenue with refund net-out</Bullet>
        <Bullet>Break out by gift cards, discounts, and add-ons</Bullet>
      </ul>
      <PathHint>Financials → Revenue</PathHint>
    </Card>
  </div>
);

const ExpensesContent = () => (
  <div>
    <SectionHeader icon={Receipt} title="Expenses" subtitle="Log costs, keep your books clean." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Log expenses with category, vendor, and date</Bullet>
        <Bullet>Attach receipts (image or PDF)</Bullet>
        <Bullet>Recurring expense support</Bullet>
        <Bullet>CSV export at tax time</Bullet>
      </ul>
      <PathHint>Financials → Expenses</PathHint>
    </Card>
  </div>
);

const PartnersContent = () => (
  <div>
    <SectionHeader icon={UserPlus} title="Partners" subtitle="Split revenue automatically." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Connect a partner to your services and the platform splits their share
        server-side at payment — they get paid directly via Stripe Connect.
      </p>
      <ul className="space-y-2">
        <Bullet>Revenue partners — tied to specific services or all of them</Bullet>
        <Bullet>Affiliates — paid only on referred bookings via referral link</Bullet>
        <Bullet>Each partner connects their own Stripe — they manage their own payouts</Bullet>
      </ul>
      <PathHint>Financials → Partners</PathHint>
    </Card>
  </div>
);

const BrandingContent = () => (
  <div>
    <SectionHeader icon={Sliders} title="Branding" subtitle="Make it look like you, not us." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Upload logo and accent color — applied across widgets, emails, tickets</Bullet>
        <Bullet>Customize email templates with merge fields</Bullet>
        <Bullet>Hide BookingFlows branding entirely (white-label)</Bullet>
      </ul>
      <PathHint>Customers → Branding</PathHint>
    </Card>
  </div>
);

const WaiversContent = () => (
  <div>
    <SectionHeader icon={Shield} title="Waivers" subtitle="Legally captured signatures." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Customers sign on their own device after booking. Every signature captures the
        legal name, drawn signature, timestamp (UTC), IP address, device, and the exact
        waiver version they agreed to.
      </p>
      <ul className="space-y-2">
        <Bullet>Custom waiver text per organization</Bullet>
        <Bullet>Version tracking — old signatures stay tied to the version signed</Bullet>
        <Bullet>Required before check-in if you toggle it on</Bullet>
        <Bullet>Export any signature as PDF for legal records</Bullet>
      </ul>
      <PathHint>Customers → Waivers</PathHint>
    </Card>
  </div>
);

const ReviewsContent = () => (
  <div>
    <SectionHeader icon={Star} title="Reviews" subtitle="Drive social proof on autopilot." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Reviews come in two ways: the post-trip Thank-You email asks for one
        automatically, and customers can leave them from their ticket page. High-rated
        reviews surface on your marketplace profile.
      </p>
      <ul className="space-y-2">
        <Bullet>Auto-prompt via the post-trip email</Bullet>
        <Bullet>Public reviews on your marketplace listing</Bullet>
        <Bullet>Reply privately or publicly</Bullet>
      </ul>
      <PathHint>Customers → Reviews</PathHint>
    </Card>
  </div>
);

const CheckInContent = () => (
  <div>
    <SectionHeader icon={QrCode} title="Check-In" subtitle="Get guests on board fast." />
    <Card>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">QR scanner</div>
          <div className="text-xs text-gray-500 mt-0.5">Barcode gun reads customer ticket</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Phone camera</div>
          <div className="text-xs text-gray-500 mt-0.5">Open the tab, point, scan</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Manual</div>
          <div className="text-xs text-gray-500 mt-0.5">Tap check-in inside the booking</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Self check-in</div>
          <div className="text-xs text-gray-500 mt-0.5">Customer scans your venue QR</div>
        </div>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed mt-4">
        Every check-in is timestamped on the calendar, the customer ticket, and Relations
        history. No-shows are tracked separately so you can spot patterns.
      </p>
      <PathHint>Customers → Check-In</PathHint>
    </Card>
  </div>
);

const RelationsContent = () => (
  <div>
    <SectionHeader icon={Users} title="Relations" subtitle="Customers, leads, and lifetime value." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Every booker rolls up into a single contact with booking history, lifetime
        spend, waivers signed, and tags. One contact per email — even if they used
        different names.
      </p>
      <ul className="space-y-2">
        <Bullet>Filter by status: lead, customer, returning, VIP</Bullet>
        <Bullet>Lifetime value, last-booking date, no-show rate</Bullet>
        <Bullet>Bulk export to CSV for email marketing</Bullet>
        <Bullet>Lead capture from the widget for cart abandonment recovery</Bullet>
      </ul>
      <PathHint>Customers → Relations</PathHint>
    </Card>
  </div>
);

const DiscountsContent = () => (
  <div>
    <SectionHeader icon={Tag} title="Discounts" subtitle="Promo codes, smartly capped." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Percent or fixed-amount codes</Bullet>
        <Bullet>Usage caps and expiry dates</Bullet>
        <Bullet>Per-service eligibility and minimum order amount</Bullet>
        <Bullet>Affiliate variant — commission tracked automatically</Bullet>
      </ul>
      <PathHint>Customers → Discounts</PathHint>
    </Card>
  </div>
);

const GiftCardsContent = () => (
  <div>
    <SectionHeader icon={Gift} title="Gift Cards" subtitle="Sell credit, redeem at checkout." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Customers buy gift cards from your public storefront</Bullet>
        <Bullet>Codes redeem inside the booking widget</Bullet>
        <Bullet>Partial redemption supported — remaining balance carries over</Bullet>
        <Bullet>Stripe minimum enforced so the card never fully zeroes a payment</Bullet>
      </ul>
      <PathHint>Customers → Gift Cards</PathHint>
    </Card>
  </div>
);

const NotificationsContent = () => (
  <div>
    <SectionHeader icon={Bell} title="Notifications" subtitle="Email + SMS, on autopilot." />
    <Card>
      <div className="space-y-3">
        {[
          ['Booking confirmation', 'Sent the moment payment succeeds — magic link to the ticket included.'],
          ['Reminder (24h before)', 'Cron-triggered. Respects your toggle here.'],
          ['Reschedule / cancel', 'Auto-sent when the booking changes on the calendar.'],
          ['Thank-you + review', 'Sent after the experience. Drives Google/Yelp reviews.'],
          ['Owner alerts', 'You get pinged on new bookings, refunds, and lead drop-offs.'],
        ].map(([title, desc]) => (
          <div key={title} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
            </div>
          </div>
        ))}
      </div>
      <PathHint>Customers → Notifications</PathHint>
    </Card>
  </div>
);

const MarketplaceContent = () => (
  <div>
    <SectionHeader icon={Store} title="Marketplace" subtitle="Your public BookingFlows profile." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A destination page guests can browse, share, and book straight from. Pulled
        automatically from your branding and active services — no separate page to
        maintain.
      </p>
      <ul className="space-y-2">
        <Bullet>Auto-uses your logo, accent color, and active services</Bullet>
        <Bullet>Toggle which services appear on the public listing</Bullet>
        <Bullet>Direct link you can share, plus search-friendly SEO</Bullet>
      </ul>
      <PathHint>Marketing → Marketplace</PathHint>
    </Card>
  </div>
);

const BlogContent = () => (
  <div>
    <SectionHeader icon={PenTool} title="SEO Blog" subtitle="AI-drafted articles for search visibility." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        AI generates SEO-tuned posts built from your services and location. Every draft
        is fully editable — you choose what publishes and where.
      </p>
      <ul className="space-y-2">
        <Bullet>Topic suggestions based on your niche</Bullet>
        <Bullet>Drafts include meta description and structured headings</Bullet>
        <Bullet>Publish to your site, status: draft / published / archived</Bullet>
      </ul>
      <PathHint>Marketing → SEO</PathHint>
    </Card>
  </div>
);

const ConversionsContent = () => (
  <div>
    <SectionHeader icon={Target} title="Conversions" subtitle="Track what's working." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        See how many widget viewers convert, how big the average booking is, and which
        channels drive the most bookings. Tie Google Ads conversion events so your
        ad spend is measurable.
      </p>
      <ul className="space-y-2">
        <Bullet>Funnel: viewers → started → completed → checked-in</Bullet>
        <Bullet>Channel breakdown (organic, social, referral, partner)</Bullet>
        <Bullet>Google Ads conversion setup guide</Bullet>
      </ul>
      <PathHint>Marketing → Conversions</PathHint>
    </Card>
  </div>
);

const SettingsContent = () => (
  <div>
    <SectionHeader icon={Settings} title="Settings" subtitle="Account, security, configuration." />
    <Card>
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          ['Profile', 'Business name, contact, timezone'],
          ['Calendar', 'Business hours, blackout dates, iCal feed'],
          ['Integrations', 'Stripe, email, calendar sync'],
          ['2FA & passkeys', 'Stronger logins for you and your team'],
          ['Webhooks', 'Outbound HTTPS events'],
          ['API Keys', 'Programmatic access'],
          ['Activity log', 'Every action stamped with who and when'],
          ['Danger zone', 'Delete data, reset billing — irreversible'],
        ].map(([t, d]) => (
          <div key={t} className="rounded-lg bg-gray-50 p-3">
            <div className="text-sm font-medium text-gray-900">{t}</div>
            <div className="text-xs text-gray-500 mt-0.5">{d}</div>
          </div>
        ))}
      </div>
      <PathHint>Sidebar → Settings</PathHint>
    </Card>
  </div>
);

const MyProfileContent = () => (
  <div>
    <SectionHeader icon={UserCheck} title="My Profile" subtitle="Your personal account settings." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Update your name, email, and contact info</Bullet>
        <Bullet>Change your password or add a passkey</Bullet>
        <Bullet>Subscribe to your trip calendar via iCal/Google</Bullet>
        <Bullet>See your activity log</Bullet>
      </ul>
      <PathHint>Sidebar → My Profile</PathHint>
    </Card>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 * Tab definitions — keyed by dashboard navigation IDs.
 * `navId` is the activeView value the dashboard expects.
 * `blurb` is the welcome-page one-liner.
 * ────────────────────────────────────────────────────────────────────── */

const TAB_HELP = {
  home: { label: 'Dashboard', icon: LayoutDashboard, blurb: "Today's bookings, revenue, and leads.", component: HomeContent },
  roles: { label: 'Roles', icon: Shield, blurb: 'Define positions and per-tab permissions.', component: RolesContent },
  teamMembers: { label: 'Team', icon: Users, blurb: 'Invite staff and manage their access.', component: TeamContent },
  assets: { label: 'Assets', icon: Package, blurb: 'Boats, venues, vehicles — your bookable inventory.', component: AssetsContent },
  services: { label: 'Services', icon: Zap, blurb: 'Build the bookable experiences customers buy.', component: ServicesContent },
  mySchedule: { label: 'My Schedule', icon: CalendarCheck, blurb: 'Just the trips assigned to you.', component: ScheduleMineContent },
  schedule: { label: 'Bookings', icon: Calendar, blurb: 'Every trip on the calendar.', component: BookingsContent },
  assignments: { label: 'Assignments', icon: TicketCheck, blurb: 'Claim trips you can run.', component: AssignmentsContent },
  flows: { label: 'Flows', icon: Sparkles, blurb: 'Your embeddable booking widgets.', component: FlowsContent },
  payments: { label: 'Payments', icon: CreditCard, blurb: 'Stripe status, payouts, and balances.', component: PaymentsContent },
  profitLoss: { label: 'P&L', icon: PieChart, blurb: 'Profit and loss, month over month.', component: ProfitLossContent },
  revenue: { label: 'Revenue', icon: TrendingUp, blurb: 'Where the money came from.', component: RevenueContent },
  expenses: { label: 'Expenses', icon: Receipt, blurb: 'Costs, receipts, and tax-ready exports.', component: ExpensesContent },
  partners: { label: 'Partners', icon: UserPlus, blurb: 'Split revenue with partners automatically.', component: PartnersContent },
  branding: { label: 'Branding', icon: Sliders, blurb: 'Logo, color, email and ticket styling.', component: BrandingContent },
  waivers: { label: 'Waivers', icon: Shield, blurb: 'Legally captured digital signatures.', component: WaiversContent },
  reviews: { label: 'Reviews', icon: Star, blurb: 'Auto-collected social proof.', component: ReviewsContent },
  checkin: { label: 'Check-In', icon: QrCode, blurb: 'Four ways to get guests on board.', component: CheckInContent },
  relations: { label: 'Relations', icon: Users, blurb: 'Customer CRM with lifetime value.', component: RelationsContent },
  discounts: { label: 'Discounts', icon: Tag, blurb: 'Promo codes and affiliate codes.', component: DiscountsContent },
  giftCards: { label: 'Gift Cards', icon: Gift, blurb: 'Sell credit, redeem at checkout.', component: GiftCardsContent },
  notifications: { label: 'Notifications', icon: Bell, blurb: 'Email + SMS automations.', component: NotificationsContent },
  marketplace: { label: 'Marketplace', icon: Store, blurb: 'Your public BookingFlows profile.', component: MarketplaceContent },
  blog: { label: 'SEO', icon: PenTool, blurb: 'AI-drafted articles for organic search.', component: BlogContent },
  conversions: { label: 'Conversions', icon: Target, blurb: 'Funnel, channels, and ad tracking.', component: ConversionsContent },
};

const ADMIN_EXTRA_HELP = {
  settingsHelp: { label: 'Settings', icon: Settings, component: SettingsContent },
};

const TEAM_EXTRA_HELP = {
  account: { label: 'My Profile', icon: UserCheck, component: MyProfileContent },
};

/* ─────────────────────────────────────────────────────────────────────────
 * HelpPage — role-aware modal
 * ────────────────────────────────────────────────────────────────────── */

const HelpPage = ({
  isOpen,
  onClose,
  initialSection = null,
  onNavigate,
  isAdmin = true,
  navGroups = [],
}) => {
  const progress = useSetupProgress({ enabled: isAdmin });

  // Build the list of dashboard tab IDs available to this user.
  const availableNavIds = useMemo(() => {
    const ids = [];
    navGroups.forEach((g) => {
      if (g.isStandalone) ids.push(g.id);
      else if (g.items) g.items.forEach((it) => ids.push(it.id));
    });
    return ids;
  }, [navGroups]);

  // Resolve which content tabs the help guide should expose.
  // - Admins always see ALL tab help (Setup as home, every section)
  // - Team members see only help for the tabs they actually have access to,
  //   plus a Welcome home and a My Profile section.
  const sections = useMemo(() => {
    if (isAdmin) {
      const all = {};
      // Setup goes first
      all.setup = { label: 'Setup', icon: Rocket, isHome: true };
      // Walk the dashboard nav order so help mirrors the sidebar
      navGroups.forEach((g) => {
        const collect = (id) => {
          if (TAB_HELP[id]) all[id] = TAB_HELP[id];
        };
        if (g.isStandalone) collect(g.id);
        else if (g.items) g.items.forEach((it) => collect(it.id));
      });
      // Admin-only "Settings" help
      Object.entries(ADMIN_EXTRA_HELP).forEach(([k, v]) => {
        all[k] = v;
      });
      return all;
    }

    // Team member view — welcome home, then mirror their actual tabs
    const tm = {};
    tm.welcome = { label: 'Welcome', icon: Hand, isHome: true };
    availableNavIds.forEach((id) => {
      if (TAB_HELP[id]) tm[id] = TAB_HELP[id];
    });
    Object.entries(TEAM_EXTRA_HELP).forEach(([k, v]) => {
      tm[k] = v;
    });
    return tm;
  }, [isAdmin, navGroups, availableNavIds]);

  const homeId = isAdmin ? 'setup' : 'welcome';
  const [activeSection, setActiveSection] = useState(initialSection || homeId);

  // Keep section in range if the user's permissions change between opens
  useEffect(() => {
    if (!sections[activeSection]) {
      setActiveSection(homeId);
    }
  }, [sections, activeSection, homeId]);

  useEffect(() => {
    if (initialSection && initialSection !== activeSection && sections[initialSection]) {
      setActiveSection(initialSection);
    }
    // React only to parent-driven section changes
  }, [initialSection]);

  const tabsList = Object.entries(sections).map(([id, def]) => ({
    id,
    label: def.label,
    icon: def.icon,
  }));

  // For team-member welcome page, build the tile list of their tabs
  const welcomeTabs = useMemo(() => {
    return availableNavIds
      .filter((id) => TAB_HELP[id])
      .map((id) => ({ id, navId: id, ...TAB_HELP[id] }));
  }, [availableNavIds]);

  const renderSection = () => {
    if (activeSection === 'setup' && isAdmin) {
      return <SetupHome progress={progress} onNavigate={onNavigate} onClose={onClose} />;
    }
    if (activeSection === 'welcome' && !isAdmin) {
      return (
        <WelcomeHome
          availableTabs={welcomeTabs}
          onNavigate={onNavigate}
          onClose={onClose}
        />
      );
    }
    const def = sections[activeSection];
    const Component = def?.component;
    if (Component) return <Component />;
    // Fallback to home
    return isAdmin ? (
      <SetupHome progress={progress} onNavigate={onNavigate} onClose={onClose} />
    ) : (
      <WelcomeHome availableTabs={welcomeTabs} onNavigate={onNavigate} onClose={onClose} />
    );
  };

  // Setup-tab badge for admins (only)
  const tabsForSidebar = tabsList.map((t) =>
    t.id === 'setup' && isAdmin && progress.remainingCore > 0
      ? { ...t, badge: progress.remainingCore }
      : t
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-5xl h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-xl"
        data-testid="help-modal"
      >
        {/* Mobile: horizontal scrollable tabs */}
        <div className="md:hidden bg-gray-50 overflow-x-auto flex-shrink-0">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Help</h2>
            {isAdmin && progress.remainingCore > 0 && (
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-600 text-white">
                {progress.remainingCore} to set up
              </span>
            )}
          </div>
          <div className="flex gap-1.5 p-2 min-w-max">
            {tabsList.map((tab) => {
              const isActive = activeSection === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 bg-white'
                  }`}
                  data-testid={`help-tab-mobile-${tab.id}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop: sidebar + content — no right border, matches Settings modal */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          <ModalSidebarNav
            title="Help"
            tabs={tabsForSidebar}
            activeTab={activeSection}
            onTabChange={setActiveSection}
          />
          <div className="flex-1 overflow-y-auto min-w-0 bg-white">
            <div className="p-6 sm:p-10 max-w-3xl">{renderSection()}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpPage;
